import { useState } from 'react';
import { getSectionScores, getOverallLevel } from '../lib/scoring';
import { getRoute } from '../lib/routing';
import { submitAssessment } from '../lib/api';
import { ORG_TYPES } from '../data/content/index';
import AevaraLogo from './AevaraLogo';
import { COLORS, FONTS, TRACKING } from '../styles/tokens';
import { primaryButton, inputField, contentWrap, kicker } from '../styles/shared';

function Field({ label, id, required, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontFamily: FONTS.body,
          fontSize: 13,
          fontWeight: 600,
          color: COLORS.textSecondary,
          marginBottom: 6,
          letterSpacing: '0.02em',
        }}
      >
        {label}{required && <span style={{ color: COLORS.holidayBlue, marginLeft: 3 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

export default function GateScreen({ answers, gateData, setGateData, onBack, onComplete }) {
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [focused, setFocused] = useState(null);

  function set(field, value) {
    setGateData(d => ({ ...d, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: null }));
  }

  function validate() {
    const e = {};
    if (!gateData.firstName.trim()) e.firstName = 'Required';
    if (!gateData.email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(gateData.email)) e.email = 'Enter a valid email';
    if (!gateData.company.trim()) e.company = 'Required';
    if (!gateData.orgType) e.orgType = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(evt) {
    evt.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    const scores = getSectionScores(answers);
    const route = getRoute(scores);
    const overallLevel = getOverallLevel(scores.total);
    const overallPercent = Math.round((scores.total / 44) * 100);

    const result = await submitAssessment({
      gateData,
      answers,
      scores,
      overall: { level: overallLevel, percent: overallPercent, total: scores.total, max: 44 },
      route,
      valueFocus: answers.q12?.valueFocus,
      needRoute: answers.q10?.needRoute,
    });

    if (!result.ok) {
      console.error('[Aevara Assessment] Submission failed — proceeding to results anyway:', result.error);
    }

    // Pass the row id forward so SendingScreen knows which assessment to
    // email. A failed insert passes `undefined`; SendingScreen handles that
    // by skipping the email and letting the user through to results.
    onComplete(route, overallLevel, gateData.orgType, result.ok ? result.id : undefined);
  }

  const inputStyle = (field) => ({
    ...inputField,
    borderColor: errors[field]
      ? 'rgba(239,68,68,0.7)'
      : focused === field
        ? COLORS.holidayBlue
        : 'rgba(255,255,255,0.12)',
    boxShadow: focused === field ? '0 0 0 3px rgba(42,192,208,0.15)' : 'none',
    color: COLORS.textPrimary,
  });

  return (
    <div style={{ minHeight: '100vh', background: COLORS.bgApp, padding: '0 20px', boxSizing: 'border-box' }}>
      <main id="main-content" style={{ ...contentWrap, maxWidth: 600, padding: '40px 20px 80px' }}>

        {/* Logo */}
        <div style={{ marginBottom: 40 }}>
          <AevaraLogo variant="light" height={32} />
        </div>

        {/* Header card */}
        <div style={{
          background: COLORS.blue,
          borderRadius: 10,
          padding: '32px 36px',
          marginBottom: 36,
        }}>
          <p style={{
            ...kicker,
            display: 'inline-block',
            background: COLORS.holidayBlue,
            color: COLORS.bgApp,
            margin: '0 0 12px',
            padding: '2px 10px',
            borderRadius: 4,
            fontWeight: 700,
          }}>Last Step</p>
          <h1 style={{
            fontFamily: FONTS.heading,
            fontSize: 'clamp(24px, 4vw, 32px)',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: TRACKING.headline,
            color: COLORS.white,
            margin: '0 0 12px',
            lineHeight: 1.1,
          }}>
            Your report is ready.
          </h1>
          <p style={{
            fontFamily: FONTS.body,
            fontSize: 15,
            lineHeight: 1.6,
            color: 'rgba(255,255,255,0.78)',
            margin: 0,
          }}>
            Enter your info below. We'll email you your personalized AI readiness report as a PDF and show you your results right away.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate aria-label="Contact information">
          <p className="sr-only">Fields marked with an asterisk (*) are required.</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Field label="First Name" id="firstName" required>
              <input
                id="firstName"
                type="text"
                value={gateData.firstName}
                onChange={e => set('firstName', e.target.value)}
                onFocus={() => setFocused('firstName')}
                onBlur={() => setFocused(null)}
                placeholder="Jordan"
                style={inputStyle('firstName')}
                autoComplete="given-name"
                aria-required="true"
                aria-invalid={!!errors.firstName}
                aria-describedby={errors.firstName ? 'firstName-error' : undefined}
              />
              {errors.firstName && <ErrorMsg id="firstName-error">{errors.firstName}</ErrorMsg>}
            </Field>

            <Field label="Work Email" id="email" required>
              <input
                id="email"
                type="email"
                value={gateData.email}
                onChange={e => set('email', e.target.value)}
                onFocus={() => setFocused('email')}
                onBlur={() => setFocused(null)}
                placeholder="jordan@company.com"
                style={inputStyle('email')}
                autoComplete="email"
                aria-required="true"
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'email-error' : undefined}
              />
              {errors.email && <ErrorMsg id="email-error">{errors.email}</ErrorMsg>}
            </Field>
          </div>

          <Field label="Organization Name" id="company" required>
            <input
              id="company"
              type="text"
              value={gateData.company}
              onChange={e => set('company', e.target.value)}
              onFocus={() => setFocused('company')}
              onBlur={() => setFocused(null)}
              placeholder="Acme Corp"
              style={inputStyle('company')}
              autoComplete="organization"
              aria-required="true"
              aria-invalid={!!errors.company}
              aria-describedby={errors.company ? 'company-error' : undefined}
            />
            {errors.company && <ErrorMsg id="company-error">{errors.company}</ErrorMsg>}
          </Field>

          <Field label="Website" id="website">
            <input
              id="website"
              type="text"
              inputMode="url"
              autoCapitalize="none"
              spellCheck={false}
              value={gateData.website}
              onChange={e => set('website', e.target.value)}
              onFocus={() => setFocused('website')}
              onBlur={() => setFocused(null)}
              placeholder="example.com (optional)"
              style={inputStyle('website')}
              autoComplete="url"
            />
          </Field>

          <Field label="Organization Type" id="orgType" required>
            <select
              id="orgType"
              value={gateData.orgType}
              onChange={e => set('orgType', e.target.value)}
              onFocus={() => setFocused('orgType')}
              onBlur={() => setFocused(null)}
              style={{
                ...inputStyle('orgType'),
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%238A8FA8' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 14px center',
                backgroundSize: '12px',
                paddingRight: 40,
                cursor: 'pointer',
              }}
              aria-required="true"
              aria-invalid={!!errors.orgType}
              aria-describedby={errors.orgType ? 'orgType-error' : undefined}
            >
              <option value="" style={{ background: COLORS.surface }}>Select your org type…</option>
              {ORG_TYPES.map(o => (
                <option key={o.value} value={o.value} style={{ background: COLORS.surface }}>{o.label}</option>
              ))}
            </select>
            {errors.orgType && <ErrorMsg id="orgType-error">{errors.orgType}</ErrorMsg>}
          </Field>

          <button
            type="submit"
            disabled={submitting}
            aria-busy={submitting}
            style={{
              ...primaryButton,
              width: '100%',
              justifyContent: 'center',
              marginTop: 8,
              opacity: submitting ? 0.7 : 1,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? 'Saving your details…' : 'Email Me My Report'}
          </button>

          <button
            type="button"
            onClick={onBack}
            style={{
              display: 'block',
              margin: '16px auto 0',
              background: 'none',
              border: 'none',
              fontFamily: FONTS.body,
              fontSize: 13,
              color: COLORS.textMuted,
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline',
              textUnderlineOffset: 3,
            }}
          >
            ← Back to questions
          </button>
        </form>

      </main>
    </div>
  );
}

function ErrorMsg({ id, children }) {
  return (
    <p
      id={id}
      role="alert"
      style={{
        fontFamily: FONTS.body,
        fontSize: 12,
        color: 'rgba(239,100,100,0.9)',
        margin: '5px 0 0',
      }}
    >
      {children}
    </p>
  );
}
